import type {
  OfferPageFilterType,
  OfferPagePriceMode,
  OfferPageQuickFilters,
  OfferPageRoom,
  OfferPageState,
} from 'contexts/OfferPage/offerPageStateReducer'
import type { RoomRateViews } from 'components/OfferPage/Property/singlePropertyUtils'
import type { LoyaltyEarnTotals } from 'loyalty/hooks/useGroupedLoyaltyEarnTotals'
import type { OfferPageLoyaltyEarnCalculationStatus } from './useOfferPageRoomPackageLoyaltyEarnTotals'
import { onlyDefinedProperties } from 'lib/object/objectUtils'
import type { IntlShape } from 'react-intl'
import type {
  SurfaceAtomRequest,
  SurfaceRecentCommittedStateRequest,
} from 'tripPlanner/assistant/api/surfaceSnapshotContract'
import { normalizeAssistantRoomOccupancy } from 'tripPlanner/assistant/utils/assistantRoomOccupancyText'
import { nonEmptyString } from './offerPageContextFormatting'

export interface OfferPageSnapshotRoom {
  id: string
  adults: number
  children?: number
  teenagers?: number
  infants?: number
  childrenAge?: Array<number>
  roomId?: string
  rateId?: string
}

export interface OfferPageSnapshotSelection {
  activeFilter?: OfferPageFilterType
  activeSubFilter?: OfferPageFilterType
  activeHeaderFilter?: OfferPageFilterType
  airportCode?: string
  checkInDate?: string
  checkOutDate?: string
  duration: number
  durationSelected?: boolean
  flights: boolean
  isMultiRoomConnected?: boolean
  multiRoomMode: OfferPageState['multiRoomMode']
  isMultiRoomSelectRooms?: boolean
  displayMemberPrice?: boolean
  selectedBedbankRoomId?: string
  selectedPackageKey?: string
  propertyRoomId?: string
  roomOption?: {
    roomRateId: string
    roomTypeId: string
  }
  quickFilters?: Partial<OfferPageQuickFilters>
  isAlternativeDatesFlow?: boolean
  fareClass: OfferPageState['fareClass']
  priceMode: OfferPagePriceMode
  pointsToApply?: number
  pointsHideZeroCash?: boolean
  viewedRateIdByRoomId?: Record<string, string>
  rooms: Array<OfferPageSnapshotRoom>
}

export interface OfferPageSnapshotPriceFormatContext {
  currencyCode: string
  regionCode: string
}

interface OfferPageSnapshotSourceBase {
  key: string
  selection: OfferPageSnapshotSelection
  priceFormatContext: OfferPageSnapshotPriceFormatContext
  pageUrl?: string
  /** Only set via `withOfferPageSnapshotIntl` at the turn-authority call site, so a cancellation-text helper can localise instead of falling back to English `defaultMessage`s. */
  intl?: IntlShape
  /**
   * Loyalty earn totals keyed by rate ID (property/bedbank) or package unique key (flash) --
   * a stable key rather than array position, since a room can have a variable number of rates.
   */
  roomPackageLoyaltyEarnTotals?: Record<string, LoyaltyEarnTotals | undefined>
  /** Aggregate status of `roomPackageLoyaltyEarnTotals`'s underlying calculations -- surfaced as the `packages.loyalty_status` manifest fact, mirroring `packages.rates_status`. */
  loyaltyStatus?: OfferPageLoyaltyEarnCalculationStatus
}

export interface FlashOfferPageSnapshotSource extends OfferPageSnapshotSourceBase {
  variant: 'flash'
  offer: App.HotelOffer
  packageViews: Array<App.OfferPackageOptionView>
  /** The LuxPlus+ tier whose inclusions the customer can currently see, or undefined if LuxPlus+ is disabled/not applicable -- mirrors `useOfferInclusions`'s own gating. */
  luxPlusInclusionsTier?: App.MembershipSubscriptionTier
  /** The `croInclusionValidity` Optimizely flag -- gates stripping "valid for N guests" text from inclusions, matching the real tile. */
  isInclusionsValidityEnabled?: boolean
  /** Only set once a charter bundle is actually active -- matches `FlashHotelRateOption`'s `charterBundleRoute` prop. */
  charterBundleRoute?: App.CharterBundleRoute
  /**
   * Charter departure date when available. Passed separately from check-in so
   * `getFlashCancellationPolicyView` / `getOfferFlexibleCancellationReferenceDate` can use
   * departure only for charter cancellation policies (and hide while departure is resolving).
   */
  charterDepartDate?: string
  /** Mirrors `isCharterEnabled` on the real page — required for charter policy reference-date selection. */
  isCharterModeEnabled?: boolean
}

export interface LmePropertyOfferPageSnapshotSource extends OfferPageSnapshotSourceBase {
  variant: 'lme_property'
  property: App.Property.Property
  roomRateViews: Array<RoomRateViews>
  ratesStatus?: 'loading' | 'unavailable'
  /** The `croInclusionValidity` Optimizely flag -- gates stripping "valid for N guests" text from LPC rate inclusions, matching the real tile. */
  isInclusionsValidityEnabled?: boolean
  /** The `croUrgencyOnSppRooms` Optimizely experiment -- gates the rooms-remaining urgency atom, matching `PropertyRoomOption`'s own gate. */
  isSppRoomUrgencyEnabled?: boolean
  /** Needed to resolve an LPC rate's package (for `roomPolicyDescription`) the same way `PropertyRateOption` does. */
  lpcOffer?: App.HotelOffer
}

export interface BedbankOfferPageSnapshotSource extends OfferPageSnapshotSourceBase {
  variant: 'bedbank'
  offer: App.BedbankOffer
  packageViews: Array<App.BedbankOfferPackageView>
  /** Whether the "Pay at property" feature flag is enabled for this page view. */
  isPropertyCollectEnabled: boolean
  /** Mirrors `offerFlightsEnabled`: whether flight bundling is offered at all for this booking. */
  flightsEnabled: boolean
}

export interface SppPropertyOfferPageSnapshotSource extends OfferPageSnapshotSourceBase {
  variant: 'spp_property'
  property: App.Property.Property
  roomRateViews: Array<RoomRateViews>
  ratesStatus?: 'loading' | 'unavailable'
  /** The `croInclusionValidity` Optimizely flag -- gates stripping "valid for N guests" text from LPC rate inclusions. Always a no-op for this variant (bedbank-backed rates are never `lpc`-sourced), kept only so the shared lme_property/spp_property builder code doesn't need a runtime type guard. */
  isInclusionsValidityEnabled?: boolean
  /** The `croUrgencyOnSppRooms` Optimizely experiment -- gates the rooms-remaining urgency atom, matching `PropertyRoomOption`'s own gate. */
  isSppRoomUrgencyEnabled?: boolean
  /** Always undefined for this variant -- see `isInclusionsValidityEnabled`'s comment. */
  lpcOffer?: App.HotelOffer
}

export type OfferPageAssistantSource =
  | FlashOfferPageSnapshotSource
  | LmePropertyOfferPageSnapshotSource
  | BedbankOfferPageSnapshotSource
  | SppPropertyOfferPageSnapshotSource

export interface OfferPageSnapshotContribution {
  key: string
  sourcePath: string
  visibleState?: {
    activeControl?: string
    activeDrawer?: string
    activeModal?: string
    suppressAutoFocus: boolean
  }
  recentCommittedState: Array<SurfaceRecentCommittedStateRequest>
  atoms: Array<SurfaceAtomRequest>
}

export interface OfferPageSnapshotInput {
  source: OfferPageAssistantSource
  contributions: Array<OfferPageSnapshotContribution>
}

export function withOfferPageSnapshotPageUrl(
  input: OfferPageSnapshotInput,
  pageUrl: string,
): OfferPageSnapshotInput {
  return { ...input, source: { ...input.source, pageUrl } }
}

export function withOfferPageSnapshotIntl(
  input: OfferPageSnapshotInput,
  intl: IntlShape,
): OfferPageSnapshotInput {
  return { ...input, source: { ...input.source, intl } }
}

function copyRoom(room: OfferPageRoom): OfferPageSnapshotRoom {
  // Canonicalise sparse assistant storage; age categorisation runs at format time with offer thresholds.
  const occupancy = normalizeAssistantRoomOccupancy(room)

  return onlyDefinedProperties({
    id: room.id,
    adults: occupancy.adults,
    children: occupancy.children,
    teenagers: occupancy.teenagers,
    infants: occupancy.infants,
    childrenAge: occupancy.childrenAge?.length ? [...occupancy.childrenAge] : undefined,
    roomId: room.roomId,
    rateId: room.rateId,
  })
}

export function createOfferPageSnapshotSelection(
  state: OfferPageState,
): OfferPageSnapshotSelection {
  return onlyDefinedProperties({
    activeFilter: state.activeFilter,
    activeSubFilter: state.activeSubFilter,
    activeHeaderFilter: state.activeHeaderFilter,
    airportCode: nonEmptyString(state.airport?.code),
    checkInDate: state.checkInDate,
    checkOutDate: state.checkOutDate,
    duration: state.duration,
    durationSelected: state.durationSelected,
    flights: state.flights,
    isMultiRoomConnected: state.isMultiRoomConnected,
    multiRoomMode: state.multiRoomMode,
    isMultiRoomSelectRooms: state.isMultiRoomSelectRooms,
    displayMemberPrice: state.displayMemberPrice,
    selectedBedbankRoomId: state.selectedBedbankRoomId,
    selectedPackageKey: state.selectedPackageKey,
    propertyRoomId: state.propertyRoomId,
    roomOption: state.roomOption ? {
      roomRateId: state.roomOption.roomRateId,
      roomTypeId: state.roomOption.roomTypeId,
    } : undefined,
    quickFilters: state.quickFilters !== undefined ? { ...state.quickFilters } : undefined,
    isAlternativeDatesFlow: state.isAlternativeDatesFlow,
    fareClass: state.fareClass,
    priceMode: state.priceMode,
    pointsToApply: state.pointsToApply,
    pointsHideZeroCash: state.pointsHideZeroCash,
    viewedRateIdByRoomId: state.viewedRateIdByRoomId ? { ...state.viewedRateIdByRoomId } : undefined,
    rooms: state.rooms.map(copyRoom),
  })
}
